/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { AlertColor, Button, CircularProgress, Divider, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import moment from 'moment';

import {
  DayOptions,
  DEFAULT_END_DATE,
  Paths,
  ScheduleTypes,
  SnackbarSeverity,
  timeoutValue,
} from '../../../../../Shared/Constants/App';

import { CreateScheduleFieldNames, FieldNames, ManageScheduleCreationInput } from '../AllJobs.Model';
import { IFormValues, numericToDayMap, PatternTypes, quarterOptions } from '../../AllSchedules/AllSchedules.Model';
import { IServiceItem } from '../../../../Jobs/Models/AddEditOneTimeJob.Model';

import JobManagementService from '../../../../Jobs/Services/JobManagementService';

import {
  getMonthlyQuarterlyScheduleDays,
  getSpecificDayQuarterlyScheduleDays,
  getWeeklyQuarterlyScheduleDays,
  getDefaultServiceValueForScheduleCreation,
  getTimeFormat,
  hours,
  getStoredCustomerDetails,
  getStateKey,
  getGraceMinutes,
  isDateBefore,
  toMinutes,
} from '../../../../../Shared/Utilities/utils';

import PageTitle from '../../../../../Shared/Components/Common/PageTitle/PageTitle';
import SnackbarMessage from '../../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';
import SchedulesRenderer from './SchedulesRenderer';

import theme from '../../../../../Shared/Themes/theme';
import { CustomerContainer } from '../../../../../Shared/Components/Layout/styles';
import {
  BottomBarContainer,
  buttonStyles,
  OneTimeJobContainer,
} from '../../../../Jobs/Components/AddEditOneTimeJob/AddEditOneTimeJob.Style';
import ScheduleDetails from './ScheduleDetails';
import ScheduleDatesAndTimes from './ScheduleDatesAndTimes';

const CreateSchedule: React.FC = () => {
  const { t } = useTranslation(['jobManagement']);
  const [isFrequencyOpen, setFrequencyOpen] = useState(false);
  const [selectedFrequency, setSelectedFrequency] = useState('');
  const [service, setService] = useState<IServiceItem[]>(getDefaultServiceValueForScheduleCreation());
  const [serviceTasks, setServiceTasks] = useState<any>({});

  const [isShiftTypeOpen, setShiftTypeOpen] = useState(false);
  const [selectedShiftType, setSelectedShiftType] = useState<string>();
  const [isStartTimeSelectOpen, setStartTimeSelectOpen] = useState(false);
  const [isEndTimeSelectOpen, setEndTimeSelectOpen] = useState(false);
  const [hoursList, setHoursList] = useState<any[]>([]);

  const [punchInHours, setPunchInHours] = useState<number>();
  const [punchInMinutes, setPunchInMinutes] = useState<number>();
  const [punchOutHours, setPunchOutHours] = useState<number>();
  const [punchOutMinutes, setPunchOutMinutes] = useState<number>();

  const [applyToAllQuarters, setApplyToAllQuarters] = useState(false);
  const previousQuartersDataRef = useRef<Record<string, any>>({});
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const [isWeekDropdownOpen, setIsWeekDropdownOpen] = useState(false);
  const [isDayDropdownOpen, setIsDayDropdownOpen] = useState(false);
  const [dropdownOpenStates, setDropdownOpenStates] = useState<Record<string, boolean>>({});
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [selectedWeek, setSelectedWeek] = useState('');
  const [isWeekOpen, setIsWeekOpen] = useState(false);
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState('');
  const [isDayOfWeekOpen, setDayOfWeekOpen] = useState(false);

  const [selectAnnualMonth, setSelectAnnualMonth] = useState('');
  const [selectAnnualDay, setSelectAnnualDay] = useState('');
  const [isMonthOpen, setIsMonthOpen] = useState(false);

  const [selectFirstBiAnnualMonth, setSelectFirstBiAnnualMonth] = useState('');
  const [selectSecondBiAnnualMonth, setSelectSecondBiAnnualMonth] = useState('');
  const [selectFirstBiAnnualDay, setSelectFirstBiAnnualDay] = useState('');
  const [selectSecondBiAnnualDay, setSelectSecondBiAnnualDay] = useState('');
  const [isFirstMonthOpen, setIsFirstMonthOpen] = useState(false);
  const [isSecondMonthOpen, setIsSecondMonthOpen] = useState(false);
  const [totalFTE, setTotalFTE] = useState<string>();
  const [endDateOffSet, setEndDateOffSet] = useState<number | null>();
  const [shiftEndTimeError, setShiftEndTimeError] = useState(false);
  const [shiftEndDateError, setShiftEndDateError] = useState(false);

  const [annualPayload, setAnnualPayload] = useState('');
  const [biAnnualPayload, setBiAnnualPayload] = useState('');

  const [isLoading, setIsLoading] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>(SnackbarSeverity.success);

  const location = useLocation();
  const jobsData = location?.state?.selectedRows;
  const isReassignVendor = location?.state?.isReassignVendor;

  const { control, setValue, getValues, watch, setError, clearErrors } = useForm<IFormValues>({
    defaultValues: {
      scheduleName: '',
      shiftStartTime: '',
      shiftEndTime: '',
      scheduleDays: '',
      shiftType: '',
      endDateOffset: 0,
      selectedQuarter: '',
      AnnualDay: '',
      selectedWeek: '',
      selectedDay: '',
      quarters: {},
      scheduleStartDate: null,
      scheduleEndDate: null,
    },
  });

  const watchFields = watch();

  const scheduleName = watch(CreateScheduleFieldNames.scheduleName);
  const patternType = watch('patternType');
  const monthlyPatternType = watch('monthlyPatternType');

  const startDate = watch('scheduleStartDate');
  const endDate = watch('scheduleEndDate');
  const shiftStartTime = watch('shiftStartTime');
  const shiftEndTime = watch('shiftEndTime');

  const allValues = getValues();

  const { userId = '' } = getStoredCustomerDetails() || {};

  const navigate = useNavigate();

  const { divider, createJobButton, buttonText } = buttonStyles;

  const quarterlyPayload = useCallback(() => {
    let payload;
    if (allValues?.patternType === PatternTypes.Monthly) {
      payload = getMonthlyQuarterlyScheduleDays(allValues?.quarters, applyToAllQuarters);
    } else if (allValues?.patternType === PatternTypes.Weekly) {
      payload = getWeeklyQuarterlyScheduleDays(allValues?.quarters, applyToAllQuarters);
    } else if (allValues?.patternType === PatternTypes.SpecificDay) {
      payload = getSpecificDayQuarterlyScheduleDays(allValues?.quarters, applyToAllQuarters);
    }
    return payload;
  }, [allValues]);

  const updateServiceTasks = () => {
    return service?.map((serviceItem) => {
      const { mdServiceId, tasks } = serviceItem;
      const tasksforSelectedService = serviceTasks[mdServiceId];
      const selectedTaskIds = tasks?.split(', ');
      const selectedTasksForService = tasksforSelectedService?.filter((task: any) =>
        selectedTaskIds?.includes(task?.taskId)
      );
      const formattedTasks = selectedTasksForService?.map((task: any) => ({
        taskId: task?.taskId,
        taskName: task?.taskName,
      }));
      return {
        mdServiceId: mdServiceId,
        mdServiceName: serviceItem?.name,
        tasks: formattedTasks,
      };
    });
  };

  const updateAllQuarters = (fieldType: string, value: string) => {
    quarterOptions?.forEach((q) => {
      return setValue(`quarters.${q}.${fieldType}` as any, value);
    });
  };

  const handleDayClick = (day: string) => {
    setSelectedDays((prev) => (prev?.includes(day) ? prev?.filter((d) => d !== day) : [...prev, day]));
  };

  const formatMonthly = (): string => {
    if (selectedDays?.length > 0 && !selectedWeek) {
      return selectedDays?.map((day) => `M-${day}`)?.join(',');
    }

    if (selectedWeek && selectedDayOfWeek) {
      const dayIndex = DayOptions?.findIndex((d) => d?.value === selectedDayOfWeek);
      return selectedWeek === 'last' ? `L-${dayIndex}` : `W${selectedWeek?.replace('week', '')}-${dayIndex}`;
    }

    return '';
  };

  const getFormattedScheduleDays = (scheduleType: string): string => {
    switch (scheduleType) {
      case ScheduleTypes.Annual:
        return annualPayload;

      case ScheduleTypes.BiAnnual:
        return biAnnualPayload;

      case ScheduleTypes.Quarterly:
        return quarterlyPayload() || '';

      case ScheduleTypes.Monthly:
        return formatMonthly();

      case ScheduleTypes.Fortnightly:
      case ScheduleTypes.Weekly:
        return selectedDays
          ?.map((day) => numericToDayMap.indexOf(day))
          ?.filter((day) => day >= 0)
          ?.join(',');

      case ScheduleTypes.Daily:
        return numericToDayMap.map((_, index) => index).join(',');
      default:
        return '';
    }
  };

  const showSnackbar = (severity: AlertColor, message: string) => {
    setSnackbarOpen(true);
    setSnackbarSeverity(severity);
    setSnackbarMessage(message);
  };

  const handleCreateSchedule = async () => {
    try {
      setIsLoading(true);
      const payload: ManageScheduleCreationInput = {
        jobId: jobsData?.[0]?.jobId ?? '',
        facilityId: jobsData?.[0]?.facilityId ?? null,
        workOrderId: jobsData?.[0]?.workOrderId ?? null,
        scheduleName: scheduleName,
        mdScheduleType: selectedFrequency,
        scheduleDays: getFormattedScheduleDays(selectedFrequency) || '',
        scheduleStartDate: startDate ? startDate : null,
        scheduleEndDate: endDate ? endDate : DEFAULT_END_DATE,
        shiftStartTime: shiftStartTime ? shiftStartTime : null,
        shiftEndTime: shiftEndTime ? shiftEndTime : null,
        mdShiftType: selectedShiftType,
        endDateOffset: endDateOffSet,
        punchInGraceMinutes: getGraceMinutes(punchInHours, punchInMinutes),
        punchOutGraceMinutes: getGraceMinutes(punchOutHours, punchOutMinutes),
        totalFTE: totalFTE ? parseFloat(totalFTE) : null,
        scheduleServices: updateServiceTasks(),
      };
      const response = await JobManagementService.createJobSchedule(payload, userId);
      if (response?.data && response?.data?.status === 'SUCCESS') {
        showSnackbar(SnackbarSeverity.success, t('jobManagement:scheduleCreatedSuccessfully'));
        const workOrderIds = response?.data?.workOrderId;
        setTimeout(() => {
          navigate(Paths.ManageAssociatesAndSupervisors, {
            state: {
              woScheduleMasterId: !isReassignVendor ? [response?.data?.woScheduleMasterId] : [],
              workOrderIds: isReassignVendor ? [workOrderIds] : [],
              isReassigningVendor: isReassignVendor,
              jobsData: isReassignVendor ? jobsData?.[0] : {},
            },
          });
        }, timeoutValue);
      } else {
        showSnackbar(SnackbarSeverity.error, t('jobManagement:errorOccurredWhileCreatingSchedule'));
      }
      setIsLoading(false);
    } catch (error) {
      console.error(t('jobManagement:errorOccurredWhileCreatingSchedule'), error);
    }
  };

  const handleErrorForEndTime = (isError: boolean, message = '') => {
    if (isError) {
      setError(CreateScheduleFieldNames.shiftEndTime, { type: 'validate', message });
      setShiftEndTimeError(true);
    } else {
      clearErrors(CreateScheduleFieldNames.shiftEndTime);
      setShiftEndTimeError(false);
    }
  };

  const timeValidationsError = () => {
    const { shiftStartTime, shiftEndTime } = getValues();

    if (shiftStartTime && shiftEndTime && toMinutes(shiftStartTime) > toMinutes(shiftEndTime)) {
      handleErrorForEndTime(true, t('oneTimeJob:endTimeShouldBeGreaterThanStartTime'));
    } else {
      handleErrorForEndTime(false);
    }

    return !(shiftStartTime && shiftEndTime) || toMinutes(shiftStartTime) > toMinutes(shiftEndTime);
  };

  const isDateRangeInvalid = (
    startDate: Date | string | null | undefined,
    endDate: Date | string | null | undefined
  ) => {
    if (!startDate || !endDate) {
      return false;
    }
    const start = typeof startDate === 'string' ? startDate : moment(startDate).format(FieldNames.dateFormat);
    const end = typeof endDate === 'string' ? endDate : moment(endDate).format(FieldNames.dateFormat);

    return !isDateBefore(start, end);
  };

  const handleErrorForEndDate = (isError: boolean, message = '') => {
    if (isError) {
      setError(CreateScheduleFieldNames.scheduleEndDate, { type: 'validate', message });
      setShiftEndDateError(true);
    } else {
      clearErrors(CreateScheduleFieldNames.scheduleEndDate);
      setShiftEndDateError(false);
    }
  };

  const dateValidationsError = () => {
    const { scheduleStartDate, scheduleEndDate } = getValues();

    if (isDateRangeInvalid(scheduleStartDate, scheduleEndDate)) {
      handleErrorForEndDate(true, t('oneTimeJob:endDateShouldBeGreaterThanStartDate'));
      return true;
    }

    handleErrorForEndDate(false);

    return !(startDate && endDate);
  };

  const isFormValid =
    scheduleName?.trim() &&
    selectedFrequency &&
    startDate &&
    shiftStartTime &&
    shiftEndTime &&
    totalFTE &&
    selectedShiftType &&
    updateServiceTasks()?.length > 0;

  const renderBottomBarContainer = () => {
    return (
      <BottomBarContainer>
        <Button
          disabled={isLoading || !isFormValid}
          sx={{ ...createJobButton, opacity: isLoading || !isFormValid ? '0.5' : '1', maxWidth: '14.25rem' }}
          onClick={() => handleCreateSchedule()}>
          {isLoading ? (
            <CircularProgress size={30} sx={{ color: theme.palette.common.white }} />
          ) : (
            <Typography sx={{ ...buttonText, color: theme.palette.common.white }}>
              {`${t('jobManagement:createSchedule')}`}
            </Typography>
          )}
        </Button>
      </BottomBarContainer>
    );
  };

  useEffect(() => {
    dateValidationsError();
    timeValidationsError();
  }, [watchFields]);

  useEffect(() => {
    setHoursList(
      hours?.map((hour) => {
        return {
          label: getTimeFormat(hour),
          value: hour,
        };
      })
    );
  }, []);

  useEffect(() => {
    if (selectedFrequency === ScheduleTypes.Annual) {
      setSelectAnnualMonth('');
      setSelectAnnualDay('');
      setValue('AnnualDay', '');
    }
    if (selectedFrequency === ScheduleTypes.BiAnnual) {
      setSelectFirstBiAnnualMonth('');
      setSelectSecondBiAnnualMonth('');
      setSelectFirstBiAnnualDay('');
      setSelectSecondBiAnnualDay('');
    }
  }, [selectedFrequency]);

  useEffect(() => {
    localStorage.removeItem('woScheduleMasterIds');
    localStorage.removeItem('workOrderIds');
  }, []);

  return (
    <>
      <PageTitle title={t('jobManagement:createJobSchedule')} />
      <CustomerContainer>
        <OneTimeJobContainer>
          <ScheduleDetails
            t={t}
            control={control}
            selectedShiftType={selectedShiftType}
            setSelectedShiftType={setSelectedShiftType}
            isShiftTypeOpen={isShiftTypeOpen}
            setShiftTypeOpen={setShiftTypeOpen}
            totalFTE={totalFTE}
            setTotalFTE={setTotalFTE}
            selectedFrequency={selectedFrequency}
            setSelectedFrequency={setSelectedFrequency}
            isFrequencyOpen={isFrequencyOpen}
            setFrequencyOpen={setFrequencyOpen}
          />
          <SchedulesRenderer
            control={control}
            selectFrequency={selectedFrequency}
            annualConfig={{
              selectAnnualMonth,
              setSelectAnnualMonth,
              selectAnnualDay,
              setSelectAnnualDay,
              isMonthOpen,
              setIsMonthOpen,
              setAnnualPayload,
            }}
            biAnnualConfig={{
              selectFirstBiAnnualMonth,
              setSelectFirstBiAnnualMonth,
              selectSecondBiAnnualMonth,
              setSelectSecondBiAnnualMonth,
              selectFirstBiAnnualDay,
              setSelectFirstBiAnnualDay,
              selectSecondBiAnnualDay,
              setSelectSecondBiAnnualDay,
              isFirstMonthOpen,
              setIsFirstMonthOpen,
              isSecondMonthOpen,
              setIsSecondMonthOpen,
              setBiAnnualPayload,
            }}
            quarterlyConfig={{
              patternType,
              setValue,
              getValues,
              applyToAllQuarters,
              setApplyToAllQuarters,
              previousQuartersDataRef,
              isLoading,
              updateAllQuarters,
              isMonthDropdownOpen,
              setIsMonthDropdownOpen,
              isWeekDropdownOpen,
              setIsWeekDropdownOpen,
              isDayDropdownOpen,
              setIsDayDropdownOpen,
              dropdownOpenStates,
              setDropdownOpenStates,
              getStateKey,
            }}
            monthlyConfig={{
              monthlyPatternType,
              setValue,
              selectedDays,
              handleDayClick,
              selectedWeek,
              setSelectedWeek,
              isWeekOpen,
              setIsWeekOpen,
              selectedDayOfWeek,
              setSelectedDayOfWeek,
              isDayOfWeekOpen,
              setDayOfWeekOpen,
            }}
            weeklyConfig={{
              t,
              selectedDays,
              handleDayClick,
              numericToDayMap,
            }}
          />

          {isEmpty(selectedFrequency) && <Divider sx={divider} />}
          <ScheduleDatesAndTimes
            t={t}
            control={control}
            service={service}
            setService={setService}
            serviceTasks={serviceTasks}
            setServiceTasks={setServiceTasks}
            isStartTimeSelectOpen={isStartTimeSelectOpen}
            setStartTimeSelectOpen={setStartTimeSelectOpen}
            hoursList={hoursList}
            isEndTimeSelectOpen={isEndTimeSelectOpen}
            setEndTimeSelectOpen={setEndTimeSelectOpen}
            punchInHours={punchInHours}
            setPunchInHours={setPunchInHours}
            punchInMinutes={punchInMinutes}
            setPunchInMinutes={setPunchInMinutes}
            punchOutHours={punchOutHours}
            setPunchOutHours={setPunchOutHours}
            punchOutMinutes={punchOutMinutes}
            setPunchOutMinutes={setPunchOutMinutes}
            endDateOffSet={endDateOffSet}
            setEndDateOffSet={setEndDateOffSet}
            customerId={jobsData?.[0]?.customerId}
            jobEndDate={jobsData?.[0]?.jobEndDate}
            shiftEndTimeError={shiftEndTimeError}
          />
        </OneTimeJobContainer>
        {renderBottomBarContainer()}
      </CustomerContainer>
      <SnackbarMessage
        open={snackbarOpen}
        successMessage={snackbarMessage}
        errorMessage={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
};

export default CreateSchedule;
